// Build facts the console chrome shows: the version chip on the rail and the env
// pill in the header (design D:8382 "V.1.0.2", D:8725 "Staging"). Both come from
// build configuration, never a literal (GAP §4 lists the design's hardcoded
// "V.1.0.2 · Staging" as a tenant-blind violation): the deploy pipeline sets
// VITE_APP_ENV (development | staging | production, INF-3) and VITE_APP_VERSION;
// vite.config.ts supplies the version in every build (CI stamp, else the root
// package.json version + short git commit; scripts/app-version).

export type AppEnvironment = 'development' | 'staging' | 'production';

function readEnv(): AppEnvironment | null {
  const raw = import.meta.env.VITE_APP_ENV;
  if (raw === 'development' || raw === 'staging' || raw === 'production') return raw;
  return import.meta.env.DEV ? 'development' : null;
}

/** The deployment environment, or null when the build did not say. */
export const appEnvironment: AppEnvironment | null = readEnv();

/** The env pill shows only off production — production needs no warning chip. */
export function envPillFor(env: AppEnvironment | null): Exclude<AppEnvironment, 'production'> | null {
  return env === 'development' || env === 'staging' ? env : null;
}

/** A stamped version, or null for none / the unreleased 0.0.0 placeholder (no chip). */
export function versionOf(raw: string | undefined): string | null {
  const v = (raw ?? '').trim().replace(/^v(?=\d)/i, '');
  return /^\d+\.\d+(?:\.\d+)?(?:[-+][0-9A-Za-z.+-]+)?$/.test(v) && !/^0\.0(?:\.0)?(?:[-+]|$)/.test(v) ? v : null;
}

/** The build version (vite.config.ts inlines the CI stamp or the root package version). */
export const appVersion: string | null = versionOf(import.meta.env.VITE_APP_VERSION);

/** The short commit the build came from, shown in the chip's tooltip; null when unknown. */
export const appCommit: string | null = import.meta.env.VITE_APP_COMMIT || null;

/** The chip's tooltip value: "0.1.0 (50365df)", or just the version. */
export const appVersionDetail: string | null = appVersion && (appCommit ? `${appVersion} (${appCommit})` : appVersion);
